/**
 * DesktopPracticeShell: the desktop practice UX (layout). Behaviour is the
 * phone shell's usePracticeShell, so the two follow the same rules; the
 * primitives (dock, groups, slider, song picker) are the phone ones too.
 * Desktop has room, so nothing hides while playing: a left panel holds the
 * controls and the score fills the rest. The panel can be collapsed for a
 * score-only view with a small floating strip.
 */
import React, { useEffect, useRef, useState } from 'react';
import MobileDock from '../mobile/MobileDock';
import BlendSlider from '../mobile/BlendSlider';
import PanelGroups from '../mobile/PanelGroups';
import MobileSongSheet from '../mobile/MobileSongSheet';
import LyricsOverlay from '../mobile/LyricsOverlay';
import { usePracticeShell } from '../mobile/usePracticeShell';
import { PlayIcon, PauseIcon, StopIcon, ChevronUpIcon, ChevronDownIcon } from '../mobile/icons';
import strings from '../mobile/strings';
import SelectMePartDialog from '../SelectMePartDialog';
import SettingsControls from '../SettingsControls';
import OnScoreTipOverlay from '../OnScoreTipOverlay';
import { canPerformAction } from '../../utils/permissionUtils';
import { useGradedTakes, describePass } from './useGradedTakes';
import GradeInspector from './GradeInspector';
import RangeGraph from './RangeGraph';
import PitchMonitor from './PitchMonitor';
import { primeMonitorAudio } from './usePitchMonitor';
import { useScratchRecorder } from '../ScratchRecorder';
import { useAudioDevices } from './audioDevices';
import { logGrading, spoken } from './practiceLog';
import '../../styles/mobile-practice.css';
import '../../styles/desktop-practice.css';

// Right-click menu over the score: tips visibility and, for editors, tip
// management.
const ScoreContextMenu = ({ menu, user, tipsVisible, onToggleTips, onEditTip, onDeleteTip, onCreateTip }) => {
  if (!menu?.visible) return null;
  const canEdit = user && user.id && canPerformAction(user, 'create-tip');
  return (
    <div className="dps-context-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={onToggleTips}>{tipsVisible ? '👁️ Hide tips' : '👁️ Show tips'}</button>
      {canEdit && menu.clickedTip && canPerformAction(user, 'edit-tip') && (
        <button type="button" onClick={onEditTip}>✏️ Edit tip</button>
      )}
      {canEdit && menu.clickedTip && canPerformAction(user, 'delete-tip') && (
        <button type="button" onClick={onDeleteTip}>🗑️ Delete tip</button>
      )}
      {canEdit && !menu.clickedTip && menu.clickPosition && (
        <button type="button" onClick={onCreateTip}>📌 Create tip here</button>
      )}
    </div>
  );
};

// Status of Grade my pitch over the score: ready, listening, or the latest
// pass with Hide / Clear.
const GradeBanner = ({ grading, partName }) => {
  const { listening, livePass, passes, hasGrades, visible } = grading;
  const current = passes.find(p => p.pass === livePass) || passes[passes.length - 1];
  let text;
  if (listening) {
    text = `🎤 ${strings.gradeListening} · pass ${livePass + 1}`;
    if (current && current.pass === livePass) text += ` · ${current.good} of ${current.graded} in tune`;
  } else if (current) {
    const { headline, detail } = describePass(current, grading.tuning);
    text = detail ? `${headline} · ${detail}` : headline;
  } else {
    text = `🎤 Grading ${partName || 'my part'}: ${strings.gradeReady}`;
  }
  return (
    <div className="dps-grade-banner" role="status">
      <span>{text}</span>
      {hasGrades && !listening && (
        <>
          <button type="button" onClick={() => grading.setVisible(!visible)}>{visible ? strings.gradeHide : strings.gradeShow}</button>
          <button type="button" onClick={grading.clear}>{strings.gradeClear}</button>
        </>
      )}
    </div>
  );
};

const DesktopPracticeShell = ({ active, controls, children }) => {
  const {
    user, fileName, title, onClose, isLoading, onSongSelect, onLocalFileLoad, onRequireSignIn,
    isPlaying, canPlay, canStop, onPlay, onPause, onStop, onTempoAdjust,
    metronomeEnabled, setMetronomeEnabled, partControls, zoom,
    playerState, playerActions, setStatus, getCurrentFileContent, loadMusicXMLFromContent,
    hasLoadedFile, currentPresetName, hoverPlay, setHoverPlay, wholeChord,
    // desktop-only score features
    getOsmd, scoreDisplayRef, getMeasureHighlighter, showScore, tipsVisible, tipAvailability,
    areAllPartsVisible, getVisibleParts, tipRefreshTrigger, isInTipPlacementMode, handleTipsLoaded,
    handleContextMenu, contextMenu, handleToggleTips, handleEditTip, handleDeleteTip, handleCreateTip
  } = controls;

  const shell = usePracticeShell({ active, controls, ui: 'desktop_shell' });
  const { inPractice, beat, hasMe, mePending, mePartName, openMePart, closeMePart, selectMePart,
    meDialogVisible, songsOpen, setSongsOpen, blend, transport, show, zoomControl, section, tempo,
    partsOverride, setPartsOverride, stepHints, setStepHints, lyrics, magnify, hasSection } = shell;

  // Grade my pitch (Record group): while it is on, every Play counts in and
  // a highlighted section plays once
  const grading = useGradedTakes({ active, controls, hasMe });
  const play = grading.playOptions ? () => onPlay(grading.playOptions) : onPlay;

  // Scratch recorder (Record group). Kept here rather than in the panel so a
  // take survives collapsing the panel and R works with the panel hidden.
  // Record with playback starts the song the way Play does.
  const recorder = useScratchRecorder({ active, fileName, inPractice, onPlay: play, onStop, controls });

  // The mic and output chosen at the bottom of the Record group
  const devices = useAudioDevices(active);

  // Pitch monitor (Record group): sing and see what the grader hears
  const [monitorOpen, setMonitorOpen] = useState(false);
  useEffect(() => { if (!active) setMonitorOpen(false); }, [active]);
  // One activity log entry per use of the monitor, when it closes
  const monitorLogRef = useRef({ openedAt: null, user, fileName });
  monitorLogRef.current.user = user;
  monitorLogRef.current.fileName = fileName;
  useEffect(() => {
    const entry = monitorLogRef.current;
    if (monitorOpen) {
      entry.openedAt = Date.now();
    } else if (entry.openedAt !== null) {
      const seconds = (Date.now() - entry.openedAt) / 1000;
      entry.openedAt = null;
      logGrading({ user: entry.user, fileName: entry.fileName, description: `Used the pitch monitor (${spoken(seconds)})`, metadata: { event: 'pitch_monitor', seconds: Math.round(seconds) } });
    }
  }, [monitorOpen]);

  // Control panel: always starts visible; hiding it lasts for this page load
  const [panelOpen, setPanelOpen] = useState(true);
  const togglePanel = () => setPanelOpen(open => !open);

  // Hover to hear (Playback group) is a mode, not a preference: while it is
  // on the transport is locked, Space does nothing and bar taps are ignored
  // (the highlighter handles those). It ends with the song or the shell.
  const hoverOn = Boolean(hoverPlay);
  const hoverFileRef = useRef(fileName);
  useEffect(() => {
    if (hoverFileRef.current === fileName && active) return;
    hoverFileRef.current = fileName;
    if (hoverPlay) setHoverPlay(false);
  }, [fileName, active, hoverPlay, setHoverPlay]);
  const dockTransport = hoverOn
    ? { ...transport, onPlay: play, canPlay: false, canPause: false, canStop: false }
    : { ...transport, onPlay: play };

  // Keyboard: Space play/pause, Escape stop, Up/Down tempo, M metronome,
  // comma/period size, P panel, R record with playback, L lyrics. Ignored while typing,
  // when a sheet is open, and with Ctrl/Cmd/Alt (so Ctrl+R still reloads).
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (songsOpen || meDialogVisible || monitorOpen) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (hoverOn) break;
          if (isPlaying) onPause();
          else if (canPlay) play();
          break;
        case 'Escape':
          if (canStop) { e.preventDefault(); onStop(); }
          break;
        case 'ArrowUp':
          e.preventDefault(); onTempoAdjust(1); break;
        case 'ArrowDown':
          e.preventDefault(); onTempoAdjust(-1); break;
        case 'm': case 'M':
          setMetronomeEnabled(!metronomeEnabled); break;
        case ',':
          zoomControl.onOut(); break;
        case '.':
          zoomControl.onIn(); break;
        case 'p': case 'P':
          togglePanel(); break;
        case 'r': case 'R':
          if (!hoverOn && !e.repeat) recorder.recordWithPlayback();
          break;
        case 'l': case 'L':
          if (lyrics.available) lyrics.setOn(!lyrics.on);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const tipsShown = showScore && tipsVisible && areAllPartsVisible?.() && zoom === 1.0;
  const osmd = getOsmd?.();

  return (
    <div className={`desktop-practice-shell ${active ? 'active' : 'inactive'} ${inPractice ? 'practice' : 'setup'}`} aria-hidden={!active}>
      <header className="dps-topbar">
        <button type="button" className="mps-icon-btn" onClick={onClose} aria-label={strings.back} title={strings.back}>&lsaquo;</button>
        <button type="button" className="mps-title tappable" onClick={() => setSongsOpen(true)} title={strings.changeSong}>
          {title}
        </button>
        {partControls.length > 1 && !mePending && (
          <button type="button" className={`mps-badge ${hasMe ? '' : 'unset'}`} onClick={openMePart}>
            {hasMe ? mePartName : strings.pickMyPart}
          </button>
        )}
        {!panelOpen && (
          <div className="dps-topbar-strip">
            <button type="button" className="mps-round mps-stop" onClick={onStop} disabled={!dockTransport.canStop} aria-label={strings.stop}>
              <StopIcon size={16} />
            </button>
            <button
              type="button"
              className={`mps-round mps-play ${beat ? `beat ${beat}` : ''}`}
              onClick={isPlaying ? onPause : play}
              disabled={isPlaying ? !dockTransport.canPause : !dockTransport.canPlay}
              aria-label={isPlaying ? strings.pause : strings.play}
            >
              {isPlaying ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
            </button>
            <div className={`dps-topbar-blend ${blend.dimmed ? 'dps-dimmed' : ''}`} title={blend.dimmed ? strings.partsOverrideOn : undefined}>
              <BlendSlider compact value={blend.value} onChange={blend.onChange} disabled={blend.disabled} pending={mePending} onDisabledTap={blend.onPickPart} />
            </div>
          </div>
        )}
        <span className="dps-spacer" />
        {recorder.isRecording && (
          <span className="dps-rec-indicator" role="status">{'●'} REC</span>
        )}
        {user && tipAvailability?.hasTips && !tipsShown && (
          <span className="dps-tips-hidden" title="This song has tips. They show at Size 100% with all parts visible.">💡</span>
        )}
        <span className="dps-shortcuts" title="Space play/pause · Esc stop · ↑↓ tempo · M metronome · , . size · P panel · R record with playback · L lyrics">⌨</span>
        <button type="button" className="mps-icon-btn dps-panel-toggle" onClick={togglePanel} aria-pressed={panelOpen} title={panelOpen ? 'Hide controls (P)' : 'Show controls (P)'}>
          {panelOpen ? <ChevronDownIcon size={22} /> : <ChevronUpIcon size={22} />}
        </button>
      </header>

      <div className="dps-body">
        {panelOpen && (
          <aside className="dps-sidebar">
            <MobileDock
              variant="inline"
              blend={blend}
              blendPending={mePending}
              show={show}
              zoom={zoomControl}
              transport={dockTransport}
              section={grading.enabled ? { ...section, once: true } : section}
              tempo={tempo}
              magnify={magnify}
            />
            <div className="dps-groups">
              <PanelGroups
                controls={controls}
                locked={inPractice}
                partsOverride={partsOverride}
                onPartsOverride={setPartsOverride}
                stepHints={stepHints}
                onStepHints={setStepHints}
                stepHintsAvailable={hasMe}
                lyrics={lyrics}
                grading={grading}
                recorder={recorder}
                devices={devices}
                onOpenMonitor={inPractice ? null : () => {
                  // Start the monitor's audio inside the click: some browsers allow it nowhere else
                  primeMonitorAudio();
                  setMonitorOpen(true);
                }}
              />
            </div>
          </aside>
        )}

        <div
          className={`dps-score ${lyrics.on && lyrics.available && lyrics.mode === 'magnify' && inPractice && !hoverOn ? 'magnifying' : ''}`}
          onContextMenu={showScore ? handleContextMenu : undefined}
        >
          {hoverOn && (
            <div className="dps-mode-banner" role="status">
              <span>{strings.hoverPlayBanner}{wholeChord ? ` \u00B7 ${strings.wholeChord}` : ''}</span>
              <button type="button" onClick={() => setHoverPlay(false)}>{strings.hoverPlayExit}</button>
            </div>
          )}
          {!hoverOn && (grading.enabled || grading.hasGrades) && (
            <GradeBanner grading={grading} partName={mePartName} />
          )}
          <PitchMonitor open={monitorOpen} onClose={() => setMonitorOpen(false)} />
          <GradeInspector
            inspect={grading.inspect}
            tuning={grading.tuning}
            onHold={grading.holdInspect}
            canHear={grading.canHear}
            hearing={grading.hearing}
            onHearTake={grading.hearTake}
            onHearTarget={grading.hearTarget}
            onStop={grading.stopHearing}
          />
          <RangeGraph
            range={grading.range}
            onHold={grading.holdRange}
            canHear={grading.canHear}
            hearing={grading.hearing}
            onHearNote={grading.hearTake}
            playsSection={grading.playsSection}
            onPlaySection={grading.playRecording}
            onStop={grading.stopHearing}
          />
          {children}
          <LyricsOverlay
            lyrics={lyrics}
            controls={controls}
            inPractice={inPractice && !hoverOn}
            clearBottom={!hoverOn && (grading.enabled || grading.hasGrades)}
            // No jumps while a section loops (like score taps) or a take is graded
            canSeek={!hasSection && !grading.enabled}
          />
          {hasLoadedFile && osmd && fileName && user && (
            <OnScoreTipOverlay
              osmdInstance={osmd}
              musicxmlId={fileName}
              isVisible={Boolean(tipsShown)}
              visibleParts={getVisibleParts?.() || []}
              onMeasureClick={getMeasureHighlighter?.()?.handleContainerClick}
              scoreDisplayRef={scoreDisplayRef}
              refreshTrigger={tipRefreshTrigger}
              isInPlacementMode={isInTipPlacementMode}
              onTipsLoaded={handleTipsLoaded}
            />
          )}
          <ScoreContextMenu
            menu={contextMenu}
            user={user}
            tipsVisible={tipsVisible}
            onToggleTips={handleToggleTips}
            onEditTip={handleEditTip}
            onDeleteTip={handleDeleteTip}
            onCreateTip={handleCreateTip}
          />

        </div>
      </div>

      {/* Per-song settings auto-save (hidden UI, see the phone shell) */}
      <div hidden>
        <SettingsControls
          playerState={playerState}
          playerActions={playerActions}
          onStatusUpdate={setStatus}
          getCurrentFileContent={getCurrentFileContent}
          loadMusicXMLFromContent={loadMusicXMLFromContent}
          hasLoadedFile={hasLoadedFile}
          currentPresetName={currentPresetName}
          isLoading={isLoading}
        />
      </div>

      {songsOpen && (
        <MobileSongSheet
          locked={inPractice}
          onSelect={onSongSelect}
          onLocalFile={onLocalFileLoad}
          onRequireSignIn={onRequireSignIn}
          onClose={() => setSongsOpen(false)}
        />
      )}

      <SelectMePartDialog
        isOpen={meDialogVisible}
        parts={partControls}
        onSelect={selectMePart}
        onClose={closeMePart}
      />
    </div>
  );
};

export default DesktopPracticeShell;
